import CustomizedProgressBar from "../../Components/LinearDeterminate";
import { usePolicyContext } from "../../Contexts/AddPolicyContext";
import ErrorComponent from "../../Components/ErrorComponent";
import { baseUrl } from "../../utilities/configurations";
import { KeyboardArrowLeft } from "@mui/icons-material";
import { useAuth } from "../../Contexts/AuthContext";
import { useEffect, useRef, useState } from "react";
import apiRequest from "../../utilities/request";
import { CircularProgress } from "@mui/material";
import DownloadQRCode from "./DownloadQRCode";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import QRCode from "react-qr-code";
import Benefits from "./Benefits";
import dayjs from "dayjs";
import "./styles.css";

const PoliciesDetails = () => {
  const { selectedPolicy, handleBack } = usePolicyContext();
  const [imageToggled, setImageToggled] = useState(false);
  const [loadState, setLoadState] = useState("");
  const [tabIndex, setTabIndex] = useState("0");
  const { setUserData } = useAuth();
  const qrRef = useRef(null);

  const handleChangeTab = (e, val) => {
    setTabIndex(val);
  };

  const getPolicy = async (state) => {
    try {
      setLoadState(state || "load");
      const res = await apiRequest(baseUrl + `/policy/policies`, "GET");
      if (!res.error) {
        setUserData(res.data || []);
        setLoadState("load success");
      } else if (res.status >= 500) {
        setLoadState("ISE");
      } else {
        setLoadState("load failed");
      }
    } catch (error) {
      setLoadState("timeout");
    }
  };

  useEffect(() => {
    getPolicy();
  }, []);

  const groupedBenefits = [];
  selectedPolicy.plan.benefits.forEach((benefit) => {
    const existingGroup = groupedBenefits.find(
      (group) => group.coverage === benefit.coverage
    );

    if (existingGroup) {
      existingGroup.benefits.push(benefit);
    } else {
      groupedBenefits.push({
        coverage: benefit.coverage,
        benefits: [benefit],
      });
    }
  });

  const toggleImage = () => {
    setImageToggled(!imageToggled);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      // Enter or Space key
      toggleImage();
    }
  };

  function capitalizeWords(name) {
    if (typeof name !== "string") {
      throw new TypeError("Expected a string");
    }
    return name
      .split(" ") // Split the name into words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
      .join(" "); // Join the words back into a single string
  }

  function formatSuffix(suffix) {
    if (suffix === "JR." || suffix === "SR.") {
      return suffix.charAt(0).toUpperCase() + suffix.slice(1).toLowerCase();
    }
    return suffix; // No change for Roman numerals or other suffixes
  }

  let content = null;
  if (loadState === "load") {
    content = (
      <div className="w-full flex justify-center items-center p-3">
        <CircularProgress />
      </div>
    );
  } else if (["ISE", "timeout", "retry"].includes(loadState)) {
    content = (
      <ErrorComponent
        onRetry={() => getPolicy("retry")}
        isLoading={loadState === "retry"}
      />
    );
  } else if (tabIndex === "0") {
    content = selectedPolicy && (
      <div className="p-3 flex flex-col">
        <div className="flex flex-col items-center">
          <div className="text-center text-[#383F49]">
            Tap the card to view the QR code
          </div>

          <div
            className="flip-container mt-2"
            role="button"
            tabIndex="0"
            onKeyDown={handleKeyDown}
            onClick={() => {
              toggleImage();
            }}
          >
            <div className={`flip-card ${imageToggled ? "flipped" : ""}`}>
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="card-content absolute bottom-3 left-2 text-[#1F2328] sm:text-sm text-[0.50rem] ml-3">
                    <p className="text-gradient sm:text-xl text-sm font-bold">
                      {selectedPolicy.cardNo
                        .replace(/\D/g, "")
                        .replace(/(.{4})/g, "$1 ")
                        .trim()}
                    </p>
                    <p className="text-[#BAD5FD] sm:text-sm text-[0.60rem] mt-2">
                      {`${capitalizeWords(
                        selectedPolicy.firstName
                      )} ${capitalizeWords(
                        selectedPolicy.middleName
                      )} ${capitalizeWords(selectedPolicy.lastName)} ${
                        selectedPolicy.suffixName !== ""
                          ? formatSuffix(selectedPolicy.suffixName)
                          : ""
                      }`}
                    </p>
                    <div className="text-[#BAD5FD] sm:text-sm text-[0.60rem]">
                      {selectedPolicy.corpName.charAt(0).toUpperCase() +
                        selectedPolicy.corpName.slice(1).toLowerCase()}
                    </div>
                  </div>
                </div>
                <div className="flip-card-back">
                  <div className="card-content absolute top-0 left-0 right-0 flex justify-between items-start p-2">
                    <div className="flex-grow sm:text-xs text-[0.55rem] text-[#FFFFFF] mt-14 ml-2">
                      <div className="w-full flex flex-col">
                        <div className="flex flex-row">
                          Plan Type:
                          <p className="ml-1 sm:text-xs text-[0.55rem] text-[#FFFFFF]">
                            {selectedPolicy.plan.planType}
                          </p>
                        </div>
                        <div className="flex flex-row">
                          Description:
                          <p className="ml-1 sm:text-xs text-[0.55rem] text-[#FFFFFF]">
                            {selectedPolicy.plan.planDescription}
                          </p>
                        </div>
                        <div className="flex flex-row">
                          Expiration:
                          <p className="ml-1 sm:text-xs text-[0.55rem] text-[#FFFFFF]">
                            {dayjs(selectedPolicy.plan.expiryDate).format(
                              "MMMM D, YYYY"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col justify-between items-end text-[#252C32] sm:mt-5 mt-5 mr-2">
                      <div className="sm:text-xs text-[0.55rem] italic mr-2">
                        CVV
                      </div>
                      <div className="qr-container mt-5">
                        <QRCode
                          value={selectedPolicy.cardNo}
                          ref={qrRef}
                          size={85}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="relative w-full max-w-xs h-8 flex flex-row items-center justify-center border border-[#D6D7D8] rounded-md mt-3 mb-3 transition ease-in-out delay-150 hover:scale-105 cursor-pointer p-2"
            onClick={() => DownloadQRCode(qrRef, selectedPolicy.cardNo)}
          >
            <div className="absolute hidden">
              <QRCode value={selectedPolicy.cardNo} ref={qrRef} size={85} />
            </div>

            <div>
              <img
                src={require("../../assets/download.png")}
                alt="Download QR Code"
                className="w-5 h-5"
              />
            </div>

            <div className="text-[#383F49] ml-2 text-sm md:text-base">
              Download QR Code
            </div>
          </div>

          <div className="w-full flex flex-col border border-[#D6D7D8] rounded-md my-2 p-4">
            <div className="font-bold">Maxicare card limits</div>

            <div className="w-full my-2">
              <CustomizedProgressBar className="mx-0" value={100} />
            </div>

            <div>
              <div className="text-xs text-[#60656D]">
                {selectedPolicy.plan.remainingBalance !== null &&
                selectedPolicy.plan.remainingBalance !== ""
                  ? `Remaining Balance: ₱${selectedPolicy.plan.remainingBalance}`
                  : null}
              </div>

              <div className="w-full flex flex-row mt-1">
                <div className="text-xs text-[#60656D]">
                  {selectedPolicy.plan.limitType}
                </div>

                <div className="text-xs text-[#60656D]">: ₱</div>

                <div className="text-xs text-[#60656D]">
                  {new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(Number(selectedPolicy.plan.limitAmt))}
                </div>
              </div>
            </div>
          </div>

          {selectedPolicy.plan.benefits &&
          selectedPolicy.plan.benefits.length > 0 ? (
            <div className="w-full flex flex-col border border-[#D6D7D8] rounded-md mt-4 p-4">
              <div className="font-bold">
                Condition specific health allowance
              </div>
              <div className="ml-5">
                {selectedPolicy.plan.benefits.map((benefit, index) => (
                  <div key={index} className="my-4">
                    <div className="text-[#1F2328] text-sm font-semibold">
                      {benefit.description}
                    </div>

                    <div className="text-xs text-[#60656D] mt-1">
                      Amount Limit: ₱
                      {new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(Number(benefit.amountLimit))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  } else if (tabIndex === "1") {
    content = (
      <div className="p-3 flex flex-col">
        <div className="w-full flex flex-col border border-[#D6D7D8] rounded-md p-4">
          <div className="w-full grid grid-cols-1 divide-y">
            <div className="flex flex-col">
              <div className="text-[#0947AB] font-semibold">MyMaxicare</div>

              <div className="text-sm text-[#383F49] mt-2">
                A full HMO program for self-employed individualsor families,
                tailor-fit to their budget and needs
              </div>
            </div>

            {selectedPolicy?.plan?.coverages?.filter(
              (coverage) => coverage.trim() !== ""
            ).length > 0 ? (
              <div className="mt-3">
                <div className="mt-2">
                  <div>
                    {selectedPolicy.plan.coverages
                      .filter((coverage) => coverage.trim() !== "") // Filter out empty strings
                      .map((coverage, cIndex) => (
                        <div
                          key={cIndex}
                          className="flex flex-row items-center mt-1"
                        >
                          <img
                            className="w-6 h-6"
                            src={require("../../assets/check.png")}
                            alt="check"
                          />
                          <div className="text-sm text-[#1F2328] ml-1">
                            {capitalizeWords(coverage)}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  } else if (tabIndex === "2") {
    content = (
      <div>
        <Benefits />
      </div>
    );
  } else {
    content = (
      <div className="p-3 flex flex-col">
        <div className="w-full flex flex-col border-2 border-[#E3E4E5] rounded-md">
          <div className="p-4">
            <div className="font-semibold">Contact information</div>

            <div className="mt-3">
              <div className="text-sm text-[#60656D]">Mobile number</div>

              <div className="text-[#A3A7AB] text-sm">
                <p
                  style={{ display: "inline" }}
                  className="text-[#1F2328] text-sm"
                >
                  {selectedPolicy.mobileNo}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col border-2 border-[#E3E4E5] rounded-md mt-4">
          <div className="p-4">
            <div className="font-semibold">Personal information</div>

            <div className="mt-3">
              <div className="mb-2">
                <div className="text-sm text-[#60656D]">Name</div>

                <div className="text-[#1F2328] text-sm">
                  {[
                    selectedPolicy.firstName,
                    selectedPolicy.middleName,
                    selectedPolicy.lastName,
                    selectedPolicy.suffixName,
                  ]
                    .map(
                      (name) =>
                        name.charAt(0).toUpperCase() +
                        name.slice(1).toLowerCase()
                    )
                    .join(" ")}
                </div>
              </div>

              <div>
                <div className="text-sm text-[#60656D]">Birthday</div>
                <div className="text-[#1F2328] text-sm">
                  {dayjs(selectedPolicy.birthDate).format("MM/DD/YYYY")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full flex flex-col border-2 border-[#E3E4E5] rounded-md mt-4">
          <div className="p-4">
            <div className="font-semibold">Card details</div>

            <div className="mt-3">
              <div className="mb-2">
                <div className="text-sm text-[#60656D]">Card number</div>

                <div className="text-[#1F2328] text-sm">
                  {selectedPolicy.cardNo
                    .replace(/\D/g, "")
                    .replace(/(.{4})/g, "$1 ")
                    .trim()}
                </div>
              </div>

              <div className="mb-2">
                <div className="text-sm text-[#60656D]">Plan type</div>

                <div className="text-[#1F2328] text-sm">
                  {selectedPolicy.plan.planType.charAt(0).toUpperCase() +
                    selectedPolicy.plan.planType.slice(1).toLowerCase()}
                </div>
              </div>

              <div className="mb-2">
                <div className="text-sm text-[#60656D]">Limit type</div>

                <div className="text-[#1F2328] text-sm">
                  {selectedPolicy.plan.limitType}
                </div>
              </div>

              <div>
                <div className="text-sm text-[#60656D]">Limit amount</div>
                <div className="w-full flex flex-row">
                  <div className="text-xs text-[#1F2328]">₱</div>

                  <div className="text-xs text-[#1F2328] ml-1">
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(Number(selectedPolicy.plan.limitAmt))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center min-h-screen max-w-screen sm:bg-[#E7EEFA] ">
      <div className="bg-white w-screen mb-[56px] sm:mb-24 lg:mb-12 sm:mt-12 sm:w-148 h-auto rounded-xl">
        <div className="w-full grid grid-cols-1 divide-y">
          <div className="p-4">
            <KeyboardArrowLeft
              className="absolute cursor-pointer"
              onClick={handleBack}
            />

            <div className="text-center font-semibold text-[#1F2328]">
              Maxicare Card
            </div>
          </div>

          <Tabs
            sx={{ width: "100%" }}
            value={tabIndex}
            onChange={handleChangeTab}
            aria-label="policies-tab"
          >
            <Tab
              className="grow md:grow-0"
              sx={{ textTransform: "none", fontSize: "13px" }}
              value="0"
              label="E-Card"
            />
            <Tab
              className="grow md:grow-0"
              sx={{ textTransform: "none", fontSize: "13px" }}
              value="1"
              label={"Coverage"}
            />
            {selectedPolicy?.groupType?.trim().toUpperCase() !== "C" && (
              <Tab
                className="grow md:grow-0"
                sx={{ textTransform: "none", fontSize: "13px" }}
                value="2"
                label={"Benefits"}
              />
            )}
            <Tab
              className="grow md:grow-0"
              sx={{ textTransform: "none", fontSize: "13px" }}
              value="3"
              label={"Account"}
            />
          </Tabs>

          <div>
            {tabIndex === "0" ? (
              <div className="p-2">{content}</div>
            ) : tabIndex === "1" ? (
              <div className="p-2">{content}</div>
            ) : tabIndex === "2" ? (
              <div className="p-2">{content}</div>
            ) : tabIndex === "3" ? (
              <div className="p-2">{content}</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoliciesDetails;
